import { create } from 'zustand';

import type { Message, Thread, Artifact } from 'types/maxi-ai';

interface ChatState {
  messages: Message[];
  threads: Thread[];
  threadId: string | null;
  artifacts: Record<string, Artifact>;
  isLoading: boolean;
  isExpanded: boolean;
  isMockMode: boolean;
}

interface MaxiAIStore extends ChatState {
  // Message management
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateMessage: (id: string, updater: (message: Message) => Message) => void;
  updateMessages: (updater: (messages: Message[]) => Message[]) => void;

  // Thread management
  setThreads: (threads: Thread[]) => void;
  setThreadId: (threadId: string | null) => void;

  // Artifact management
  setArtifact: (id: string, artifact: Artifact) => void;
  updateArtifact: (
    id: string,
    updater: (artifact: Artifact) => Artifact
  ) => void;
  clearArtifacts: () => void;

  // UI state
  setIsLoading: (isLoading: boolean) => void;
  setIsExpanded: (isExpanded: boolean) => void;

  // Mock Mode (for POC)
  setIsMockMode: (isMockMode: boolean) => void;

  // Utilities
  clearChat: () => void;
  reset: () => void;
}

const initialState: ChatState = {
  messages: [],
  threads: [],
  threadId: null,
  artifacts: {},
  isLoading: false,
  isExpanded: false,
  isMockMode: false,
};

export const useMaxiAIStore = create<MaxiAIStore>()((set) => ({
  ...initialState,

  // Message operations
  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateMessage: (id, updater) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? updater(msg) : msg
      ),
    })),

  updateMessages: (updater) =>
    set((state) => ({
      messages: updater(state.messages),
    })),

  // Thread operations
  setThreads: (threads) => set({ threads }),
  setThreadId: (threadId) => set({ threadId }),

  // Artifact operations
  setArtifact: (id, artifact) =>
    set((state) => ({
      artifacts: { ...state.artifacts, [id]: artifact },
    })),

  updateArtifact: (id, updater) =>
    set((state) => {
      const existingArtifact = state.artifacts[id];
      if (existingArtifact) {
        return {
          artifacts: { ...state.artifacts, [id]: updater(existingArtifact) },
        };
      }
      return state;
    }),

  clearArtifacts: () => set({ artifacts: {} }),

  // UI state operations
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsExpanded: (isExpanded) => set({ isExpanded }),

  // Mock mode operations
  setIsMockMode: (isMockMode) => set({ isMockMode }),

  // Cleanup operations
  clearChat: () =>
    set({
      messages: [],
      threadId: null,
      artifacts: {},
      isMockMode: false,
    }),

  reset: () => set(initialState),
}));
